import { useState, useEffect } from 'react'
import { useGoogleSheetPayItemConnectionQuery } from 'schema'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useEditGoogleSheetPayItemConnection } from '../../../../../../hooks/useEditGoogleSheetPayItemConnection'
import { useSyncGoogleSheetPayItemConnection } from '../../../../../../hooks/useSyncGoogleSheetPayItemConnection'
import { useSummaryContext } from '../../../../SummaryContext'
import { useColumns } from './hooks/useColumns'
import { GoogleSheet, GoogleSheetTableProps } from './types'
import CimsPayItemConnectionEntityTable from '../CimsPayItemConnectionEntityTable'
import Warning from '../../../../../Warning'
import { useExpand } from './hooks/useExpand'
import { GoogleSheetRecord } from './GoogleSheetRecord'

const GoogleSheetTable: React.FC<GoogleSheetTableProps> = ({
  connectionId,
}) => {
  const { globalId } = useSummaryContext()
  const { data, loading, refetch } = useGoogleSheetPayItemConnectionQuery({
    ...DefaultQueryHookOptions,
    variables: globalId ? { connectionId, globalId } : undefined,
    skip: !globalId,
  })
  const [title, setTitle] = useState<string>()
  const [error, setError] =
    useState<GoogleSheet['connection']['lastSyncError']>(null)
  const [records, setRecords] = useState<GoogleSheetRecord[]>()
  useEffect(() => {
    if (!data?.googleSheetPayItemConnection) return
    const { title, compositeId, connection, headers } =
      data.googleSheetPayItemConnection
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Stop-gap for existing behavior.
    setError(connection.lastSyncError)
    setTitle(title ?? compositeId)
    setRecords(
      data.googleSheetPayItemConnection.records.cells.reduce((records, row) => {
        records.push(new GoogleSheetRecord(headers, row))
        return records
      }, Array<GoogleSheetRecord>())
    )
  }, [data?.googleSheetPayItemConnection])
  const sync = useSyncGoogleSheetPayItemConnection({ wait: true })
  const edit = useEditGoogleSheetPayItemConnection()
  return (
    <CimsPayItemConnectionEntityTable
      rowKey={'rowKey'}
      columns={useColumns(data?.googleSheetPayItemConnection?.headers)}
      records={records}
      refetch={refetch}
      loading={loading}
      expand={useExpand()}
      headers={data?.googleSheetPayItemConnection?.headers?.map(
        ({ formattedValue: label, ...rest }) => ({ label, ...rest })
      )}
      onSync={(connectionId) =>
        sync({ variables: { input: { connectionId, wait: true } } })
      }
      onEdit={({ connectionId, headers }) =>
        edit({ variables: { input: { connectionId, headers } } })
      }
      connectionName={title}
      additionalCustomHeaderActions={
        data
          ? [
              {
                type: 'externalLink',
                label: (
                  <Warning visible={!!error} tooltip={error?.message}>
                    {title}
                  </Warning>
                ),
                href: data.googleSheetPayItemConnection.url,
              },
            ]
          : undefined
      }
    />
  )
}

export default GoogleSheetTable
